import Layout from "@/components/Layout";
import { ambassadors } from "@/data/content";
import { useLang } from "@/context/LanguageContext";
import { Instagram, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function AmbassadorsPage() {
  const { t, lang } = useLang();

  const handleApply = () => {
    toast.success(
      lang === "en"
        ? "Application started — we'll redirect you to the form."
        : "Přihláška zahájena — přesměrujeme tě na formulář.",
      {
        description:
          lang === "en"
            ? "Connect with @seoulvibes on Instagram to apply."
            : "Napiš na @seoulvibes na Instagramu.",
      }
    );
  };

  return (
    <Layout>
      <section className="container-sv pt-24 pb-16">
        <div className="flex items-center gap-3 mb-4">
          <span className="w-8 h-px" style={{ background: "var(--sv-purple)" }} />
          <span className="font-display uppercase tracking-[0.35em] text-[10px] text-white/60">
            Program
          </span>
        </div>
        <h1
          className="font-display font-black uppercase text-5xl md:text-7xl tracking-tight leading-[0.92]"
          data-testid="merch-page-title"
        >
          <span className="text-white">Seoul</span>
          <span className="neon-text-purple">Vibes</span>{" "}
          <span className="text-white">{t.pages.merch.title}</span>
        </h1>
        <p className="mt-5 text-white/65 font-body text-lg max-w-2xl">
          {t.pages.merch.subtitle}
        </p>
      </section>

      {/* Apply CTA strip */}
      <section className="container-sv pb-20">
        <div
          className="relative rounded-3xl border border-white/10 overflow-hidden p-8 md:p-12 grid grid-cols-1 md:grid-cols-12 gap-8 items-center"
          style={{
            background:
              "radial-gradient(ellipse at 10% 50%, rgba(168,85,247,0.22), transparent 60%), radial-gradient(ellipse at 90% 50%, rgba(236,72,153,0.18), transparent 60%), #0c0c0e",
          }}
        >
          <div className="md:col-span-8">
            <h2 className="font-display font-black uppercase text-3xl md:text-5xl tracking-tight leading-tight text-white">
              {t.pages.merch.applyTitle}
            </h2>
            <p className="mt-3 text-white/70 font-body max-w-xl">
              {t.pages.merch.applyBody}
            </p>
          </div>
          <div className="md:col-span-4 md:justify-self-end">
            <button
              onClick={handleApply}
              data-testid="ambassador-apply-cta"
              className="sv-btn sv-btn-primary"
            >
              {t.pages.merch.applyCta} <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* Current ambassadors grid */}
      <section className="container-sv pb-32">
        <div className="flex items-center gap-3 mb-8">
          <span className="w-8 h-px bg-white/30" />
          <span className="font-display uppercase tracking-[0.35em] text-[10px] text-white/50">
            {t.pages.merch.currentTitle}
          </span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-6">
          {ambassadors.map((a) => (
            <div
              key={a.id}
              className="group relative rounded-2xl overflow-hidden border border-white/5 hover:border-purple-400/40 transition-all"
              data-testid={`ambassador-card-${a.id}`}
            >
              <div className="aspect-[3/4] bg-black relative">
                <img
                  src={a.image}
                  alt={a.name}
                  className="absolute inset-0 w-full h-full object-cover opacity-85 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <h3 className="font-display font-bold uppercase text-white text-sm tracking-tight">
                    {a.name}
                  </h3>
                  <div className="mt-1 flex items-center justify-between text-[10px] uppercase tracking-[0.2em] font-display text-white/60">
                    <span className="flex items-center gap-1.5">
                      <Instagram className="w-3 h-3" />
                      {a.handle}
                    </span>
                    <span className="text-neon-purple" style={{ color: "var(--sv-purple)" }}>
                      {a.city}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </Layout>
  );
}
